import { normalizeResources } from './resources';
import { isDomainInvalidation } from './events';

// No store imports, global listeners or timers until explicitly enabled and invalidated.
export function createInvalidationCoordinator({ environment, debounceMs = 300, retryMs = 5000,
  maxConcurrent = 2, minRefreshMs = 10000, maxRetryMs = 120000, random = Math.random,
  isVisible = () => true, onResourcesChanged = () => {}, onState = () => {} } = {}) {
  let enabled = false;
  let disposed = false;
  let timer;
  let timerDue = Infinity;
  const owners = new Map();
  const seen = new Set();
  const changedResources = new Map();
  const resources = () => [...new Set([...owners.values()].flatMap(o => [...o.registrations.values()].flatMap(r => r.resources)))].sort();
  const blocked = owner => [...owner.registrations.values()].some(r => r.isBlocked());
  const report = () => {
    if (!disposed) onState({ pending: [...owners.values()].filter(o => o.dirty).length,
      blocked: [...owners.values()].filter(o => o.dirty && blocked(o)).length,
      failed: [...owners.values()].filter(o => o.dirty && o.failed).length,
      refreshing: [...owners.values()].filter(o => o.running).length });
  };
  const schedule = delay => {
    if (!enabled || disposed || !isVisible()) return;
    const due = Date.now() + Math.max(0, delay);
    if (timer !== undefined && timerDue <= due) return;
    clearTimeout(timer);
    timerDue = due;
    timer = setTimeout(() => { timer = undefined; timerDue = Infinity; void flush(); }, Math.max(0, delay));
  };
  async function run(owner) {
    const registrations = [...owner.registrations.values()];
    // Draft protection belongs to an individual owner, not to unrelated screens.
    const target = registrations[registrations.length - 1];
    if (!target || owner.running || !owner.dirty || blocked(owner) || !isVisible()) return;
    if (owner.nextAt > Date.now()) { schedule(owner.nextAt - Date.now()); return; }
    owner.dirty = false;
    const controller = new AbortController();
    owner.controller = controller;
    owner.target = target;
    owner.running = true;
    let failed = false;
    try { failed = (await target.refresh({ signal: controller.signal })) === false; }
    catch { failed = true; }
    finally {
      owner.running = false;
      owner.failed = failed;
      owner.failures = failed ? (owner.failures || 0) + 1 : 0;
      const retryDelay = Math.min(maxRetryMs, retryMs * (2 ** Math.min(owner.failures - 1, 8)) * (1 + random() * 0.2));
      owner.nextAt = Date.now() + (failed ? retryDelay : minRefreshMs);
      if (enabled && !disposed && owners.get(owner.key) === owner) {
        owner.dirty = owner.dirty || (!controller.signal.aborted && failed);
        schedule(debounceMs);
        report();
      }
    }
  }
  async function flush() {
    if (!enabled || disposed || !isVisible()) return;
    clearTimeout(timer); timer = undefined; timerDue = Infinity;
    const available = maxConcurrent - [...owners.values()].filter(o => o.running).length;
    const waiting = [...owners.values()].filter(o => o.dirty && !o.running && !blocked(o));
    const ready = waiting.filter(o => !(o.nextAt > Date.now())).slice(0, Math.max(0, available));
    for (const owner of waiting) {
      if (owner.nextAt > Date.now()) schedule(owner.nextAt - Date.now());
    }
    await Promise.all(ready.map(run));
    report();
  }
  function invalidate(changed, remember = true) {
    if (!enabled || disposed) return;
    const wanted = new Set(normalizeResources(changed));
    if (remember) wanted.forEach(resource => changedResources.set(resource, Date.now()));
    for (const owner of owners.values()) {
      if ([...owner.registrations.values()].some(r => r.resources.some(resource => wanted.has(resource)))) owner.dirty = true;
    }
    schedule(debounceMs); report();
  }
  return {
    resources,
    register({ key, resources: requested, refresh, isBlocked = () => false, freshSince = 0 }) {
      if (disposed) return () => {};
      if (typeof key !== 'string' || !key || typeof refresh !== 'function') throw new Error('INVALID_REFRESH_OWNER');
      const mapped = normalizeResources(requested);
      let owner = owners.get(key);
      if (!owner) { owner = { key, registrations: new Map(), dirty: false, running: false }; owners.set(key, owner); }
      const token = Symbol(key);
      if (owner.running) { owner.controller?.abort(); owner.dirty = true; }
      owner.registrations.set(token, { resources: mapped, refresh, isBlocked });
      if (enabled) {
        onResourcesChanged(resources());
        // Registration is not a data change. Initial loaders already own the
        // first snapshot. Only replay actual changes missed before registration.
        if (mapped.some(resource => changedResources.has(resource) && changedResources.get(resource) >= freshSince)) {
          owner.dirty = true; schedule(debounceMs); report();
        }
      }
      let released = false;
      return () => {
        if (released) return;
        released = true;
        if (owner.running && owner.registrations.get(token) === owner.target) {
          owner.controller?.abort(); owner.dirty = true;
        }
        owner.registrations.delete(token);
        if (!owner.registrations.size) { owner.controller?.abort(); if (owners.get(key) === owner) owners.delete(key); }
        if (enabled && !disposed) { onResourcesChanged(resources()); schedule(0); report(); }
      };
    },
    setEnabled(value) {
      if (disposed || enabled === Boolean(value)) return;
      enabled = Boolean(value);
      if (!enabled) {
        clearTimeout(timer); timer = undefined; timerDue = Infinity; seen.clear(); changedResources.clear();
        owners.forEach(o => { o.controller?.abort(); o.dirty = false; });
      } else onResourcesChanged(resources());
    },
    receive(event) {
      if (!enabled || disposed || !isDomainInvalidation(event, environment) || seen.has(event.eventId.toLowerCase())) return false;
      seen.add(event.eventId.toLowerCase());
      if (seen.size > 500) seen.delete(seen.values().next().value);
      invalidate(event.invalidates.map(x => x.resource)); return true;
    },
    invalidate,
    catchUp: () => invalidate(resources(), false),
    flush,
    resume: () => { if (enabled) schedule(0); },
    dispose() {
      disposed = true; enabled = false; clearTimeout(timer);
      owners.forEach(o => o.controller?.abort()); owners.clear(); seen.clear(); changedResources.clear();
    },
  };
}
